import React from "react";
import TextField, { TextFieldProps as MuiTextFieldProps } from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import type { SxProps, Theme } from "@mui/material/styles";
import styles from "./CustomTextField.module.css";

export type CustomTextFieldVariant = "outlined" | "filled" | "standard";

export type CustomTextFieldState =
  | "enabled"
  | "focused"
  | "error"
  | "disabled";

export interface CustomTextFieldProps
  extends Omit<MuiTextFieldProps, "variant" | "sx"> {
  variant?: CustomTextFieldVariant;
  state?: CustomTextFieldState;
  typeNumber?: boolean;
  helper?: boolean | React.ReactNode;
  adornStart?: boolean;
  adornEnd?: boolean;
  startInstance?: React.ReactNode;
  endInstance?: React.ReactNode;
  sx?: SxProps<Theme>;
}

const joinClassNames = (...classNames: Array<string | undefined | null | false>) =>
  classNames.filter(Boolean).join(" ");

export const CustomTextField = ({
  variant = "outlined",
  state = "enabled",
  typeNumber = false,
  helper,
  adornStart = false,
  adornEnd = false,
  startInstance,
  endInstance,
  helperText,
  InputProps,
  disabled,
  error,
  focused,
  size = "small",
  fullWidth = true,
  className,
  type,
  sx,
  ...props
}: CustomTextFieldProps) => {
  const resolvedDisabled = disabled || state === "disabled";
  const resolvedError = error || state === "error";
  const resolvedFocused = !resolvedDisabled && (focused || state === "focused");

  const helperContent = (() => {
    if (helper === false) {
      return undefined;
    }

    if (React.isValidElement(helper) || typeof helper === "string" || typeof helper === "number") {
      return helper;
    }

    if (helper === true) {
      return helperText ?? "Helper text";
    }

    return helperText;
  })();

  const resolvedStartAdornment =
    InputProps?.startAdornment ??
    (adornStart && startInstance ? (
      <InputAdornment position="start">{startInstance}</InputAdornment>
    ) : undefined);

  const resolvedEndAdornment =
    InputProps?.endAdornment ??
    (adornEnd && endInstance ? (
      <InputAdornment position="end">{endInstance}</InputAdornment>
    ) : undefined);

  const hasAdornment = Boolean(resolvedStartAdornment || resolvedEndAdornment);

  return (
    <TextField
      {...props}
      type={typeNumber ? "number" : type}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={resolvedDisabled}
      error={resolvedError}
      focused={resolvedFocused}
      helperText={helperContent}
      sx={sx}
      InputProps={{
        ...InputProps,
        startAdornment: resolvedStartAdornment,
        endAdornment: resolvedEndAdornment,
      }}
      className={joinClassNames(
        styles.root,
        fullWidth && styles.fullWidth,
        size === "small" && styles.sizeSmall,
        size === "medium" && styles.sizeMedium,
        variant === "outlined" && styles.variantOutlined,
        variant === "filled" && styles.variantFilled,
        state === "focused" && styles.stateFocused,
        state === "error" && styles.stateError,
        resolvedDisabled && styles.stateDisabled,
        hasAdornment && styles.inputWithAdornment,
        className,
      )}
    />
  );
};

export default CustomTextField;
